<template>
    <div>
        <div v-if="isCanvasser" class="selectBox">
            <p>{{$t('REGISTER_SELECT_BOX_SIZE')}}</p>
            <select-box
                v-if="availableProducts.length && showSelectBox"
                v-model="selectedProduct"
                :options="availableProducts"
                :selected="defaultProductSelected"/>
        </div>

        <dislike-lily-check-box v-model="dislikeLily" :label="$t('Dislike Lily ?')" v-if="showDislikeLily" @update:modelValue="dislikeLilyValue"/>

        <div class="delivery-plan text-center" v-if="displayPlanDropdown">
            <p> {{ $t('DELIVERY_ADDRESS_CHOOSE_PLAN') }} </p>
            <multiselect
                openDirection="below"
                :allow-empty="false"
                :options="plans"
                v-model="selectedPlan"
                :inputStyle="'border-color: grey;'"
                :track-by="'plan_interval'"
                :label="'plan_name'"
                :placeholder="'Select a plan'" />
            <p class="cancel-plan-text"> {{$t('DELIVERY_ADDRESS_CANCEl_PLAN')}} </p>
        </div>

        <!-- Postcode -->
        <postcode-lookup-field
            class="contentsquare_sensitive_data"
            ref="postcode"
            :mandatory="true"
            address-type="shipping"
            v-model="address.postcode"
            @clear-address="clearAddress"
            @address-selected="updateSelectedAddress"/>

            <p
                class="text-center underline cursor-pointer"
                @click="showFullAddress = !showFullAddress"
                v-if="!showFullAddress"> {{$t('DELIVERY_ADDRESS_ENTER_MANUALLY')}}
            </p>

        <!-- Address line 1 -->
        <div>
            <input-field
                class="mb-3 contentsquare_sensitive_data"
                id="delivery_address_line_1"
                v-show="showFullAddress"
                :mandatory="true"
                autocomplete="shipping address-line1"
                v-model="address.addressLine1"
                @change="addressChanged"
                :is-valid="!errorHandler.has('delivery_address_line_1')"
                :message="errorHandler.get('delivery_address_line_1')"
                :label="$t('DELIVERY_ADDRESS_FIRST_LINE')"/>
        </div>

        <!-- Address line 2 -->
        <div>
            <input-field
                class="mb-3 contentsquare_sensitive_data"
                v-show="showFullAddress"
                autocomplete="shipping address-line2"
                v-model="address.addressLine2"
                @change="addressChanged"
                :label="$t('DELIVERY_ADDRESS_SECOND_LINE')"/>
        </div>

        <!-- City -->
        <div>
            <input-field
                class="mb-3 contentsquare_sensitive_data"
                id="delivery_address_city"
                v-show="showFullAddress"
                :mandatory="true"
                autocomplete="shipping address-level2"
                v-model="address.city"
                @change="addressChanged"
                :is-valid="!errorHandler.has('delivery_address_city')"
                :message="errorHandler.get('delivery_address_city')"
                :label="$t('DELIVERY_ADDRESS_TOWN_OR_CITY')"/>
        </div>

        <delivery-waiting-list-modal
            :show-modal="showUndeliverablePostcodeModal"
            @closed="toggleUndeliverablePostcodeModal"
            :postcode="address.postcode"/>

    <!-- Delivery Day and Date -->
    <div class="delivery-details">
        <div class="delivery-details--summary">
            <div class="delivery-details--summary--image">
                <img :src="getImagetUrl" alt="Delivery Vehical image">
            </div>
            <div class="delivery-details--summary--content">
                <p class="delivery-details--summary--text" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_FIRST_DELIVERY')}}</p>
                <h2 class="delivery-details--summary--day" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_DELIVERY_DAY', {deliveryDate})}}</h2>
                <p class="delivery-details--summary--text" v-if="deliveryDay" v-html="$t('REGISTER_DELIVERY_DETAILS_FRESH_FLOWERS', {deliveryDay})"></p>
                <p class="delivery-details--summary--text" v-if="deliveryDay">{{$t('REGISTER_DELIVERY_DETAILS_CHANGE_DELIVERY_DAY')}}</p>
                <p v-if="!deliveryDay" class="delivery-details--summary--text" v-html="$t('DELIVERY_FORM_POP_IN')"/>
                <div class="delivery-details--summary--day-select" v-if="showDeliveryDaySelector">
                    <multiselect
                        class="delivery-details--summary--day-select--dropdown"
                        v-model="preferredDeliveryDay"
                        openDirection="below"
                        :searchable="false"
                        :show-labels="false"
                        :options="weekDayOptions"
                        :allow-empty="false"
                        />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import DislikeLilyCheckBox from '@/components/forms/fields/DislikeLilyCheckBox';
import InputField from '@/components/forms/fields/InputField';
import PostcodeService from '@/services/logistics/PostcodeService';
import DeliveryWaitingListModal from '@/components/modals/DeliveryWaitingListModal';
import PostcodeLookupField from '@/components/forms/fields/PostcodeLookupField';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SelectBox from '@/components/forms/register/SelectBox';
import { couriers } from '@/content/couriers';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import { isPostcodeFormatValid } from '@/helpers/PostcodeHelpers';

export default {
    name: 'DeliveryAddressForm',
    data: () => {
        return {
            address: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            showUndeliverablePostcodeModal: false,
            showFullAddress: false,
            showSelectBox: process.env.VUE_APP_SELECT_BOX_ENABLED === 'true',
            weekDayOptions: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            showDislikeLily: false,
        };
    },
    props: {
        value: {
            required: true,
            type: Object,
        },
        showAddress: {
            required: false,
            type: Boolean,
            default: false,
        },
        displayPlanDropdown: {
            required: false,
            type: Boolean,
            default: true,
        },
        oldAddress: {
            required: false,
            type: Object,
            default: null,
        },
        errorHandler: {
            required: false,
            default: () => new ErrorResponseHandler(),
            validator: function(value) {
                return value instanceof ErrorResponseHandler;
            },
        },
    },
    mounted() {
        this.$store.dispatch('register/getAllProducts');
        this.showFullAddress = this.showAddress;
        this.selectedPlan = this.plans[0];
        this.address = this.value;
    },
    methods: {
        addressChanged() {
            this.$emit('addressChanged', this.address);
        },
        updateSelectedAddress(address) {
            this.address.addressLine1 = address.addressline1;
            this.address.addressLine2 = address.addressline2;
            this.address.city = address.county;
            this.showFullAddress = true;
            this.fetchDeliveryDateFromApi(this.address.postcode);
        },
        toggleUndeliverablePostcodeModal(value) {
            this.showUndeliverablePostcodeModal = value || !this.showUndeliverablePostcodeModal;
        },
        clearAddress(value) {
            if (value) {
                this.address.addressLine1 = null;
                this.address.addressLine2 = null;
                this.address.city = null;
            }
        },
        fetchDeliveryDateFromApi(postcode) {
            if (isPostcodeFormatValid(postcode)) {
                PostcodeService.GetFirstDeliveryDayForPostcode(postcode)
                    .then(response => {
                        const data = response.data.properties;
                        this.firstPossibleDeliveryDate = data.first_delivery_day;
                        this.courierName = data.courier_name;
                        this.preferredDeliveryDay = this.deliveryDay;
                    })
                    .catch(error => {
                        const response = error.response.data;

                        if (response.errors?.postcode) {
                            this.postcodeError = response.errors.postcode[0];
                        }
                    });
            }
        },
        dislikeLilyValue(value) {
            this.dislikeLily = value;
        },
    },
    computed: {
        defaultProductSelected() {
            return this.primaryProducts?.filter(item => item.product_family.toLowerCase() === this.defaultProductFamily.toLowerCase())[0] || null;
        },
        plans() {
            return this.$store.getters['register/getAllPlans'];
        },
        defaultProductFamily() {
            return this.$store.getters['register/getDefaultProductFamily'];
        },
        primaryProducts() {
            return this.$store.getters['register/getPrimaryProducts'];
        },
        availableProducts() {
            return this.primaryProducts.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        },
        selectedPlan: {
            get() {
                return this.$store.getters['register/getSelectedPlan'] || this.plans[0];
            },
            set(value) {
                this.$store.commit('register/setSelectedPlan', value);
            },
        },
        selectedProduct: {
            get() {
                return this.$store.getters['register/getSelectedProductDetails'];
            },
            set(value) {
                this.$store.commit('register/setSelectedProductDetails', value);
            },
        },
        isCanvasser() {
            return this.$store.getters['auth/isCanvasser'];
        },
        courierName: {
            get() {
                return this.$store.getters['register/getCourierName'];
            },
            set(value) {
                this.$store.commit('register/setCourierName', value);
            },
        },
        getImagetUrl() {
            let name = couriers[this.courierName] ? couriers[this.courierName].icon : 'van';
            return require(`@/assets/images/decoration/icon-delivery-${name}.svg`);
        },
        deliveryDay() {
            if (!this.address.postcode) return null;
            if (this.showDeliveryDaySelector && this.preferredDeliveryDay) return this.preferredDeliveryDay;
            return this.$store.getters['register/getFirstDeliveryDay'];
        },
        deliveryDate() {
            const deliveryDate = this.$store.getters['register/getFirstPossibleDeliveryDate'];
            return deliveryDate && formatDate(deliveryDate, Dates.DAY_DATE_MONTH);
        },
        showDeliveryDaySelector() {
            return process.env.VUE_APP_DELIVERY_DAY_SELECTOR === 'true' && this.courierName === 'DPD';
        },
        preferredDeliveryDay: {
            get() {
                return this.$store.getters['register/getPreferredDeliveryDay'];
            },
            set(value) {
                this.$store.commit('register/setPreferredDeliveryDay', value);
            },
        },
        firstPossibleDeliveryDate: {
            get() {
                return this.$store.getters['register/getFirstPossibleDeliveryDate'];
            },
            set(value) {
                this.$store.commit('register/setFirstPossibleDeliveryDate', value);
            },
        },
        dislikeLily: {
            get() {
                return this.$store.getters['register/getDislikeLily'];
            },
            set(value) {
                this.$store.commit('register/setDislikeLily', value);
            },
        },
    },
    watch: {
        address: {
            deep: true,
            handler(address) {
                this.$emit('input', address);
                this.addressChanged();
            },
        },
        'address.postcode': function(newPostcode, oldPostcode) {
            newPostcode = (newPostcode || '').toLowerCase();
            oldPostcode = (oldPostcode || '').toLowerCase();

            if ((oldPostcode !== newPostcode) && this.$refs.postcode.isValid) {
                this.$emit('postcodeChanged');
                PostcodeService.fetchPostcodeDeliverableStatus(newPostcode)
                    .catch(() => this.toggleUndeliverablePostcodeModal(true));
            }
        },
        value: {
            immediate: true,
            handler(value) {
                this.address.addressLine1 = value.addressLine1;
                this.address.addressLine2 = value.addressLine2;
                this.address.postcode = value.postcode;
                this.address.city = value.city;
            },
        },
        selectedProduct: {
            deep: true,
            handler(value) {
                this.showDislikeLily = value.product_family.toLowerCase() === 'standard';
            },
        },
    },
    components: {
        PostcodeLookupField,
        InputField,
        DeliveryWaitingListModal,
        Multiselect,
        SelectBox,
        DislikeLilyCheckBox,
    },
};
</script>

<style lang="scss" scoped>
    .delivery-plan {
        padding-top: 25px;
        max-width: 550px;
        margin: 0 auto;
        margin-bottom: 25px;
        margin-top: 30px;
        padding-right: 15px;
        padding-left: 15px;
    }
    .selectBox {
        margin: 30px 10px;
    }
    .cancel-plan-text {
        font-size: 18px;
    }
    .delivery-details {
    margin-top: 20px;
    &--summary {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: #F7F7F7;
        border-radius: 4px;
        margin: 40px auto;
        padding-bottom: 20px;
        &--content {
            width: 70%;
            margin: 0 auto;
        }
        &--image {
            width: 100px;
            margin-top: -15px;
        }
        &--text {
            font-size: 14px;
            line-height: 140%;
            color: #262323;
            text-align: center;
        }
        &--day {
            color: #262323;
            text-align: center;
            font-size: 20px;
            line-height: 25px;
        }
        &--day-select {
            margin: 20px auto;
            &--dropdown {
                width: 50%;
                max-width: 300px;
                min-width: 150px;
                margin: 10px auto;
            }
        }
    }
}
</style>
